'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateMangalDosha, MangalDoshaResult, CreateBirthProfileInput } from '@/lib/api/client';
import { ShieldAlert, ShieldCheck, Compass, Moon } from 'lucide-react';

export interface MangalDoshaCalculatorClientProps {
  initialResult?: MangalDoshaResult | null;
  initialError?: string | null;
}

export function MangalDoshaCalculatorClient({
  initialResult = null,
  initialError = null
}: MangalDoshaCalculatorClientProps) {
  const tMangal = useTranslations('mangalDosha');

  const [result, setResult] = useState<MangalDoshaResult | null>(initialResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateMangalDosha(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || tMangal('calculationFailed'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getHouseLabel = (houseNum: number | null | undefined): string => {
    if (!houseNum || houseNum < 1 || houseNum > 12) return '-';
    try {
      return tMangal(`house${houseNum}` as any) || `${houseNum}`;
    } catch (_) {
      return `${houseNum}`;
    }
  };

  const formatSeverity = (sev: string | null | undefined): string => {
    const s = sev?.toLowerCase();
    if (s === 'high') return tMangal('severityHigh');
    if (s === 'moderate') return tMangal('severityModerate');
    if (s === 'low') return tMangal('severityLow');
    return tMangal('severityNone');
  };

  const getLocalizedDescription = (res: MangalDoshaResult): string => {
    const lagnaHouse = getHouseLabel(res.houseFromLagna);
    const moonHouse = getHouseLabel(res.houseFromMoon);

    if (res.isManglikFromLagna && res.isManglikFromMoon) {
      return tMangal('doubleManglik', { lagnaHouse, moonHouse });
    }
    if (res.isManglikFromLagna) {
      return tMangal('lagnaManglik', { lagnaHouse });
    }
    if (res.isManglikFromMoon) {
      return tMangal('moonManglik', { moonHouse });
    }
    if (res.houseFromLagna && res.houseFromMoon) {
      return tMangal('notManglik', { lagnaHouse, moonHouse });
    }
    return tMangal('unavailable');
  };

  return (
    <div className="space-y-8" data-testid="mangal-dosha-client">
      <QuickCalculatorForm
        title={tMangal('title')}
        subtitle={tMangal('subtitle')}
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div data-testid="mangal-error-panel" className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 text-rose-500 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div data-testid="mangal-result-panel" className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 shadow-xl space-y-6">
          {/* Status Header */}
          <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left border-b border-[var(--line)] pb-6">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center shrink-0 ${
              result.isManglik
                ? 'bg-rose-500/15 border border-rose-500/30 text-rose-400'
                : 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
            }`}>
              {result.isManglik ? <ShieldAlert className="w-10 h-10" /> : <ShieldCheck className="w-10 h-10" />}
            </div>
            <div className="space-y-1">
              <div className={`text-xs font-semibold uppercase tracking-wider ${
                result.isManglik ? 'text-rose-400' : 'text-emerald-400'
              }`}>
                {tMangal('statusLabel')}
              </div>
              <h2 id="mangal-status-heading" className="text-2xl sm:text-3xl font-serif font-bold text-[var(--foreground)]">
                {result.isManglik
                  ? tMangal('detected')
                  : tMangal('notDetected')}
              </h2>
              <p className="text-xs sm:text-sm text-[var(--muted)] leading-relaxed">{getLocalizedDescription(result)}</p>
            </div>
          </div>

          {/* Breakdown Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="p-4 rounded-xl border border-[var(--line)] bg-[var(--card-warm)] flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--background)] text-[var(--gold)] border border-[var(--line)]">
                <Compass className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-[var(--muted)]">
                  {tMangal('fromLagna')}
                </div>
                <div className="text-sm font-bold text-[var(--foreground)]">
                  {getHouseLabel(result.houseFromLagna)} {result.isManglikFromLagna ? ` ${tMangal('doshaIndicator')}` : ''}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-[var(--line)] bg-[var(--card-warm)] flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--background)] text-[var(--gold)] border border-[var(--line)]">
                <Moon className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-[var(--muted)]">
                  {tMangal('fromMoon')}
                </div>
                <div className="text-sm font-bold text-[var(--foreground)]">
                  {getHouseLabel(result.houseFromMoon)} {result.isManglikFromMoon ? ` ${tMangal('doshaIndicator')}` : ''}
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-[var(--line)] bg-[var(--card-warm)] flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--background)] text-[var(--gold)] border border-[var(--line)]">
                <ShieldAlert className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-[var(--muted)]">
                  {tMangal('severityLabel')}
                </div>
                <div className="text-sm font-bold text-[var(--foreground)]">
                  {formatSeverity(result.severity)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
